/**
 * A partner's Word return, read-only (WS-22): the file as the partner sent
 * it, the reviewer's issues on it beside their paragraphs — on a wide view in
 * a margin, else as a list — each kept or dismissed. Fixing one is the
 * partner's, through a rework round (Partner work in Needs you), so it isn't
 * offered here. Where the client's form is the report, the return is adopted
 * as the case's draft — edited and released from the Document view.
 */

import { useEffect, useRef, useState } from 'react';

import { Badge, Box, Button, Center, Grid, Group, Loader, Stack, Text } from '@mantine/core';
import { useElementSize } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import * as Sentry from '@sentry/react';

import { DocxContent } from '@/components/Containers/DocxContent';
import { ApiConflictError, vnextApiClient } from '@/services/vnext-api';
import type { ReviewIssue, WorkbenchDocument, WorkbenchHeader } from '@/types/workbench';
import { toError } from '@/utils/errors';
import { CONTAINER_BREAKPOINTS, MARGIN_WIDTH } from './layout';
import { IssueCard, ReviewIssues, type Resolution } from './ReviewIssues';
import { ReviewMargin, type MarginPlace } from './ReviewMargin';
import classes from './DraftView.module.css';

interface ReturnViewProps {
  header: WorkbenchHeader;
  document: WorkbenchDocument;
  /** The reviewer's issues on this return. */
  issues: ReviewIssue[];
  /** After an issue is closed (the case refetches). */
  onChanged: () => void | Promise<void>;
  /** The return adopted as the case's draft: open it. */
  onAdopted?: (draftId: string) => void | Promise<void>;
}

export function ReturnView({
  header,
  document,
  issues,
  onChanged,
  onAdopted,
}: Readonly<ReturnViewProps>) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [adopting, setAdopting] = useState(false);
  const { ref: sizeRef, width } = useElementSize();
  const margin = width >= MARGIN_WIDTH;
  const docRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    setFailed(null);
    vnextApiClient
      .getWorkbenchDocumentFile(header.reportId, 'return', document.id)
      .then((file) => {
        if (!cancelled) {
          setUrl(file.url);
        }
      })
      .catch((err: unknown) => {
        Sentry.captureException(toError(err));
        if (!cancelled) {
          setFailed(toError(err, "The return couldn't be opened").message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [header.reportId, document.id]);

  const resolve = async (issue: ReviewIssue, status: Resolution) => {
    await vnextApiClient.resolveReviewIssue(header.reportId, issue.id, status);
    await onChanged();
  };

  const adopt = async () => {
    setAdopting(true);
    try {
      const adopted = await vnextApiClient.adoptReturn(header.reportId, document.id);
      const carried =
        adopted.issuesCarried > 0
          ? ` — ${adopted.issuesCarried} review ${adopted.issuesCarried === 1 ? 'issue' : 'issues'} carried over`
          : '';
      notifications.show({
        color: 'teal',
        message: `Adopted as the draft${carried}. Edit and release it here.`,
      });
      await onAdopted?.(adopted.draftId);
      await onChanged();
    } catch (err) {
      if (!(err instanceof ApiConflictError)) {
        Sentry.captureException(toError(err));
      }
      notifications.show({
        color: 'red',
        message: toError(err, "The return couldn't be adopted").message,
      });
    } finally {
      setAdopting(false);
    }
  };

  const renderIssue = (issue: ReviewIssue, place?: MarginPlace) => (
    <IssueCard
      key={issue.id}
      issue={issue}
      selected={issue.id === selected}
      onSelect={(chosen) => setSelected(chosen.id)}
      changed={false}
      pendingEdit={false}
      canFix={false}
      fixPending={false}
      onToggleFix={() => undefined}
      onResolve={resolve}
      cardRef={place?.cardRef}
      style={place?.style}
    />
  );

  const body = (
    <Box ref={docRef}>
      {failed ? (
        <Text size="sm" c="red">
          {failed}
        </Text>
      ) : !url ? (
        <Center h={120}>
          <Loader size="sm" />
        </Center>
      ) : (
        <DocxContent key={document.id} docxUrl={url} cacheKey={`return:${document.id}`} />
      )}
    </Box>
  );

  return (
    <Grid
      ref={sizeRef}
      gutter="md"
      type="container"
      breakpoints={CONTAINER_BREAKPOINTS}
      data-testid="return-view"
    >
      <Grid.Col span={{ base: 12, lg: margin ? 12 : 8 }}>
        <Stack gap="sm">
          <Group gap={6}>
            <Badge size="sm" variant="light" color="grape">
              Partner&apos;s return
            </Badge>
            <Text size="xs" c="dimmed">
              Read-only · {document.filename}
              {document.status === 'superseded' ? ' · resubmitted since' : ''}
            </Text>
            <Button
              size="compact-xs"
              variant="default"
              loading={adopting}
              onClick={() => void adopt()}
              ml="auto"
              data-testid="return-adopt"
            >
              Use as the draft
            </Button>
          </Group>
          <Text size="xs" c="dimmed">
            Keep or dismiss the reviewer&apos;s issues here; fixing one is the partner&apos;s,
            through a rework round. Where the form is the report, use the return as the draft and
            edit it.
          </Text>
          {margin ? (
            <div className={classes.page}>
              {body}
              <ReviewMargin
                issues={issues}
                docRoot={docRef}
                selected={selected}
                renderIssue={renderIssue}
              />
            </div>
          ) : (
            body
          )}
        </Stack>
      </Grid.Col>
      {!margin && (
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <ReviewIssues issues={issues} renderIssue={(issue) => renderIssue(issue)} />
        </Grid.Col>
      )}
    </Grid>
  );
}
